import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Container,
  Nav,
  Navbar as BSNavbar,
  Button,
  NavDropdown,
} from "react-bootstrap";
import {
  FaUser,
  FaUserPlus,
  FaBars,
  FaHome,
  FaBook,
  FaInfoCircle,
  FaPhoneAlt ,
  FaCertificate,
  FaNewspaper,
  FaEnvelope,
  FaVideo,
  FaCogs,
  FaChild,
  FaLaptopCode,
  FaChalkboardTeacher,
  FaBriefcase,
  FaChevronDown,
  FaGamepad,
  FaAppStore,
  FaMicrochip,
  FaRobot,
  FaRupeeSign,
  FaProjectDiagram
} from "react-icons/fa";
import "../../styles/navbar.css";

// Import logo
import logo from "../../assets/sensible-academy-logo1.png";

const PublicNavbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [expanded, setExpanded] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [scrolled]);

  const navLinks = [
    { path: "/", label: "Home", icon: <FaHome /> },
    { path: "/courses", label: "Courses", icon: <FaBook /> },
    { path: "/projects", label: "Projects", icon: <FaProjectDiagram /> },
    { path: "/jobs", label: "Jobs", icon: <FaBriefcase /> },
  ];

  // Updates dropdown items
  const updatesDropdownItems = [
    { path: "/blog", label: "Blog", icon: <FaNewspaper /> },
    { path: "/about", label: "About Us", icon: <FaInfoCircle /> },
    { path: "/contact", label: "Contact", icon: <FaPhoneAlt  /> },
    { path: "/webinars", label: "Webinars", icon: <FaVideo /> },
    { path: "/services", label: "Services", icon: <FaCogs /> },
    { path: "/pricing", label: "Pricing", icon: <FaRupeeSign /> },
    { path: "/verify", label: "Verify Certificate", icon: <FaCertificate /> },
  ];

  const kidsDropdownItems = [
    { path: "/kids", label: "Kids", icon: <FaChild /> },
    {
      path: "/kids/app-development",
      label: "App Development",
      icon: <FaAppStore  />,
    },
    {
      path: "/kids/arduino-electronics",
      label: "Arduino & Electronics",
      icon: <FaMicrochip />,
    },
    {   
      path: "/kids/robotics-for-kids",
      label: "Robotics for Kids",
      icon: <FaRobot  />,
    },
    {
      path: "/kids/game-development",
      label: "Game Development",
      icon: <FaGamepad />,
    },
  ];

  // Training dropdown items
  const trainingDropdownItems = [
    {
      path: "/internship",
      label: "Internship Program",
      icon: <FaLaptopCode />,
    },
    {
      path: "/vocational-training",
      label: "Vocational Training",
      icon: <FaChalkboardTeacher />,
    },
  ];

  const isActive = (path) => {
    return location.pathname === path;
  };

  // Check if any dropdown item is active
  const isUpdatesDropdownActive = () => {
    return updatesDropdownItems.some((item) => location.pathname === item.path);
  };

  // Check if Training dropdown is active
  const isTrainingDropdownActive = () => {
    return trainingDropdownItems.some(
      (item) => location.pathname === item.path,
    );
  };

  const isKidsDropdownActive = () => {
    return kidsDropdownItems.some((item) => location.pathname === item.path);
  };

  // Close mobile menu when navigating
  const handleNavClick = () => {
    setExpanded(false);
  };

  return (
    <BSNavbar
      expand="lg"
      fixed="top"
      expanded={expanded}
      onToggle={setExpanded}
      className={`navbar-main-wrapper ${scrolled ? "navbar-main-scrolled" : ""}`}
    >
      <Container>
        {/* Logo */}
        <BSNavbar.Brand
          as={Link}
          to="/"
          className="navbar-main-brand"
          onClick={handleNavClick}
        >
          <img src={logo} alt="Sensible Academy" className="navbar-main-logo" />
        </BSNavbar.Brand>

        {/* Toggle Button */}
        <BSNavbar.Toggle
          aria-controls="navbar-main-nav"
          className="navbar-main-toggler"
        >
          <FaBars className="navbar-main-toggler-icon" />
        </BSNavbar.Toggle>

        {/* Navigation Links */}
        <BSNavbar.Collapse id="navbar-main-nav">
          <Nav className="ms-auto navbar-main-nav">
            {navLinks.map((link, index) => (
              <Nav.Link
                key={index}
                as={Link}
                to={link.path}
                className={`navbar-main-nav-link ${isActive(link.path) ? "active" : ""}`}
                onClick={handleNavClick}
              >
                <span className="navbar-main-nav-icon">{link.icon}</span>
                <span className="navbar-main-nav-label">{link.label}</span>
              </Nav.Link>
            ))}

            {/* Kids Dropdown */}
            <NavDropdown
              title={
                <span className="navbar-main-dropdown-title">
                  <FaChild className="navbar-main-nav-icon" />
                  <span className="navbar-main-nav-label">Kids</span>
                  <FaChevronDown className="navbar-main-dropdown-arrow" />
                </span>
              }
              id="navbar-main-kids-dropdown"
              className={`navbar-main-dropdown ${isKidsDropdownActive() ? "active" : ""}`}
              align="end"
              
            >
              {kidsDropdownItems.map((item, index) => (
                <NavDropdown.Item
                  key={index}
                  as={Link}
                  to={item.path}
                  onClick={handleNavClick}
                  className={`navbar-main-dropdown-item ${isActive(item.path) ? "active" : ""}`}
                >
                  <span className="navbar-main-dropdown-icon">{item.icon}</span>
                  <span>{item.label}</span>
                </NavDropdown.Item>
              ))}
            </NavDropdown>

            {/* Updates Dropdown */}
            <NavDropdown
              title={
                <span className="navbar-main-dropdown-title">
                  <FaEnvelope className="navbar-main-nav-icon" />
                  <span className="navbar-main-nav-label">Updates</span>
                  <FaChevronDown className="navbar-main-dropdown-arrow" />
                </span>
              }
              id="navbar-main-updates-dropdown"
              className={`navbar-main-dropdown ${isUpdatesDropdownActive() ? "active" : ""}`}
              align="end"
            >
              {updatesDropdownItems.map((item, index) => (
                <NavDropdown.Item
                  key={index}
                  as={Link}
                  to={item.path}
                  onClick={handleNavClick}
                  className={`navbar-main-dropdown-item ${isActive(item.path) ? "active" : ""}`}
                >
                  <span className="navbar-main-dropdown-icon">{item.icon}</span>
                  <span>{item.label}</span>
                </NavDropdown.Item>
              ))}
            </NavDropdown>

            {/* Training Dropdown */}
            <NavDropdown
              title={
                <span className="navbar-main-dropdown-title">
                  <FaLaptopCode className="navbar-main-nav-icon" />
                  <span className="navbar-main-nav-label">Training</span>
                  <FaChevronDown className="navbar-main-dropdown-arrow" />
                </span>
              }
              id="navbar-main-training-dropdown"
              className={`navbar-main-dropdown ${isTrainingDropdownActive() ? "active" : ""}`}
              align="end"
            >
              {trainingDropdownItems.map((item, index) => (
                <NavDropdown.Item
                  key={index}
                  as={Link}
                  to={item.path}
                  onClick={handleNavClick}
                  className={`navbar-main-dropdown-item ${isActive(item.path) ? "active" : ""}`}
                >
                  <span className="navbar-main-dropdown-icon">{item.icon}</span>
                  <span>{item.label}</span>
                </NavDropdown.Item>
              ))}
            </NavDropdown>

            {/* Auth Buttons */}
            <div className="navbar-main-auth-buttons">
              <Button
                variant="outline-primary"
                className="navbar-main-login-btn"
                onClick={() => {
                  handleNavClick();
                  navigate("/login");
                }}
              >
                <FaUser className="navbar-main-btn-icon" /> Login
              </Button>
              <Button
                variant="primary"
                className="navbar-main-register-btn"
                onClick={() => {
                  handleNavClick();
                  navigate("/register");
                }}
              >
                <FaUserPlus className="navbar-main-btn-icon" /> Register
              </Button>
            </div>
          </Nav>
        </BSNavbar.Collapse>
      </Container>
    </BSNavbar>
  );
};

export default PublicNavbar;
